import {Template} from "../../../components/template/template.jsx"
import React, {useEffect, useRef, useState} from "react";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import Filters from "./components/filters.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useMutation} from "@tanstack/react-query";
import informationQualityService from "../../../services/reports/information-quality-service.js";
import ReportUtils from "../../../utils/report-utils.js";
import reportUtils from "../../../utils/report-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import {subMonths} from "date-fns";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";

const initialFilters = () => {
  const refDate = new Date();
  return {
    dtStart: subMonths(refDate, 3),
    dtEnd: refDate,
    hrStart: new Date(refDate.setHours(0, 0, 0, 0)),
    hrEnd: new Date(refDate.setHours(23, 59, 59, 0)),
    period: {value: "all", label: "Todos"},
    archivedOccurrences: false,
  }
}

const filterLabels = {
  dtStart: "Data inicial",
  dtEnd: "Data final",
  hrStart: "Hora inicial",
  hrEnd: "Hora final",
  archivedOccurrences: "Contabilizar ocorrências arquivadas",
  period: "Período"
};

const initialTableColumns = {
  id: {label: "ID", visible: true},
  system_dt_create: {label: "D/H do cadastro", visible: true},
  problems: {label: "Problemas", visible: true}
};
const InformationQuality = () => {
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [auditFilters, setAuditFilters] = useState(null);
  const [fixedTop, setFixedTop] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState(null);
  const [title] = useState("Qualidade da informação");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const [tableColumns, setTableColumns] = useState(initialTableColumns);
  const printComponentRef = useRef(null);

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  const mutation = useMutation({
    mutationFn: (payload) => informationQualityService.apply(payload),
    onSuccess: data => setReportData(data.data),
  })

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
        hrStart: filters.hrStart ? projectUtils.formatTimeString(filters.hrStart): "",
        hrEnd: filters.hrEnd ? projectUtils.formatTimeString(filters.hrEnd): "",
        period: reportUtils.periods().find(el => el.value === filters.period.value).label
      }, filterLabels)
    );

  }, [reportData]);


  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null);
      }
    });

    const isNotValidDt = reportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd,  3,'months');
    if(!isNotValidDt.ok){
      projectUtils.swalErrorMessage("", isNotValidDt.message, "Ok");
      setLoading(false);
      return;
    }

    const isNotValid = reportUtils.validateTimeString(filters.hrStart, filters.hrEnd);
    if(!isNotValid.ok){
      projectUtils.swalErrorMessage("", isNotValid.message, "Ok");
      setLoading(false);
      return;
    }

    try {
      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
        hrStart: filters.hrStart ? filters.hrStart : null,
        hrEnd: filters.hrEnd ? filters.hrEnd : null,
        archivedOccurrences: filters.archivedOccurrences ? "Sim" : "Não"
      };

      const auditFilters = ReportUtils.filters({
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
          hrStart: formattedFilters.hrStart,
          hrEnd: formattedFilters.hrEnd,
          archivedOccurrences: formattedFilters.archivedOccurrences
        },
        filterLabels, true);

      setAuditFilters(formattedFilters);

      mutation.mutate({
        ...buildPayload(filters),
        filters: auditFilters
      });

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  }

  const clearSearch = () => {
    setFilters(initialFilters());
    setReportData(null);
  }

  function buildPayload(data){
    const payloadObj = {
      date_start: reportUtils.formatDateTime(data.dtStart),
      date_end: reportUtils.formatDateTime(data.dtEnd),
      hrStart:  projectUtils.formatTimeString(filters.hrStart),
      hrEnd:  projectUtils.formatTimeString(filters.hrEnd),
    }
    if(data.archivedOccurrences) payloadObj.archivedOccurrences = "on";

    return payloadObj
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  return(
    <SubScroll dependency={[collapsed, reportData]}>
      <div className={"flex"}>

        <Template isLoading={loading || mutation.isPending}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={true}
                exportInSheets={true}
                printable={true}
                headerChange={true}
                visibilityColumns={true}
                columns={tableColumns}
                setColumns={setTableColumns}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={reportData?.length}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>
              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>
            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
            </div>
          </Card.Body>
        </Card>

      </div>
    </SubScroll>
  )
}

export default InformationQuality;